import type { View } from './types';
import { ClubSettingsView } from '../../components/club';
import type { EquipmentSettingsCardProps } from '../../components/organization';
import { PartnerCourtsCard } from '../../components/organization/PartnerCourtsCard';
import { PartnerActivitiesCard } from '../../components/organization/PartnerActivitiesCard';
import type { MeLink } from '../../lib/api/me';
import { HomeView, MiniTournamentsView } from '../../components/home';
import { CreateTournamentView } from '../../components/tournament';

export interface HomeHubViewsProps {
  view: string;
  goHome: () => void;
  setView: (v: View) => void;
  clubInfo: any;
  /** The club's booking configuration — which courts the home widgets count (#873). */
  bookingConfig?: any;
  /** Whether `clubInfo` has been read — the editor is gated on it (#775). */
  clubInfoStatus: 'loading' | 'ready' | 'unavailable';
  /**
   * Opens the organization's «Tilganger» page, or null for an account that
   * does not manage access here (slice 3, decision 4A). Null renders no
   * entry at all: the shell decides from `/me`, the settings view only draws.
   */
  onOpenAccess: (() => void) | null;
  /**
   * Opens the organization's «Samarbeid» page, or null for an account that
   * does not manage co-operation here (slice 4a). Decided by the shell from
   * `/me`, like `onOpenAccess`.
   */
  onOpenLinks: (() => void) | null;
  /**
   * Opens the organization's «Logg» (#935), or null for an account that does
   * not read its audit trail. Decided by the shell from `/me`.
   */
  onOpenAuditLog: (() => void) | null;
  /**
   * The equipment registry for the settings' «Utstyr» card, or null for an
   * account that may not define equipment here (2026-10-21). As with
   * `onOpenAccess`: the shell decides, the view draws.
   */
  equipment: EquipmentSettingsCardProps | null;
  /**
   * The centres this person may book courts at for the club (slice 4c), from
   * `/me`'s link reach. Empty draws no door.
   */
  partnerCourts?: MeLink[];
  onOpenPartnerCourts: (host: MeLink) => void;
  /**
   * The clubs this person may arrange activities in for the centre (slice
   * 4d), from `/me`'s link reach. Empty draws no door.
   */
  partnerActivities?: MeLink[];
  onOpenPartnerActivities?: (host: MeLink) => void;
  saveClubInfo: any;
  parkCurrentTournament: any;
  setTournament: any;
  setSetupFromActivities: any;
  setCustomActivityType: any;
  setPendingSetupLoad: any;
  setEditingKMSetup: any;
  setSingleMatchesReturnView: any;
  singleMatches: any;
  setBookingInitialDate: any;
  setActivitiesOpenId: any;
  activities: any;
  bookings: any;
  tournament: any;
  resetTournament: any;
  archiveTournament: any;
  showToast: any;
  combinedArchive: any;
  archiveLoaded: any;
  openArchived: any;
  deleteArchived: any;
  effectivePlayerDB: any;
  pausedTournaments: any;
  handleResumeTournament: any;
  handlePauseRunningInList: any;
  handleArchiveFromList: any;
  handleDeletePaused: any;
  handleDeleteAllPaused: any;
  drafts: any;
  openDraft: any;
  deleteDraft: any;
  activeSeries: any;
}

/**
 * Home-hub view-dispatch group (AppShell step 4). Renders the club-settings,
 * home, create-tournament, mini-tournaments and (legacy alias) other-formats
 * views for the matching `view`; null otherwise. Branches moved verbatim from
 * the `Padel24AppMain` return JSX (extracted from the former monolith).
 */
export function HomeHubViews({
  view,
  goHome,
  setView,
  clubInfo,
  bookingConfig,
  clubInfoStatus,
  onOpenAccess,
  onOpenLinks,
  onOpenAuditLog,
  equipment,
  partnerCourts,
  onOpenPartnerCourts,
  partnerActivities,
  onOpenPartnerActivities,
  saveClubInfo,
  parkCurrentTournament,
  setTournament,
  setSetupFromActivities,
  setCustomActivityType,
  setPendingSetupLoad,
  setEditingKMSetup,
  setSingleMatchesReturnView,
  singleMatches,
  setBookingInitialDate,
  setActivitiesOpenId,
  activities,
  bookings,
  tournament,
  resetTournament,
  archiveTournament,
  showToast,
  combinedArchive,
  archiveLoaded,
  openArchived,
  deleteArchived,
  effectivePlayerDB,
  pausedTournaments,
  handleResumeTournament,
  handlePauseRunningInList,
  handleArchiveFromList,
  handleDeletePaused,
  handleDeleteAllPaused,
  drafts,
  openDraft,
  deleteDraft,
  activeSeries,
}: HomeHubViewsProps) {
  return (
    <>
      {view === 'clubSettings' && (
        <ClubSettingsView
          clubInfo={clubInfo}
          status={clubInfoStatus}
          onSave={saveClubInfo}
          onBack={goHome}
          onOpenAccess={onOpenAccess}
          onOpenLinks={onOpenLinks}
          onOpenAuditLog={onOpenAuditLog}
          equipment={equipment}
        />
      )}
      {/* Mounted only with a centre to show: the card reads the locale, and the
          home view's own tests render this group without a provider. */}
      {view === 'home' && partnerCourts?.length > 0 && (
        <PartnerCourtsCard hosts={partnerCourts} onOpen={onOpenPartnerCourts} />
      )}
      {view === 'home' && partnerActivities?.length > 0 && onOpenPartnerActivities && (
        <PartnerActivitiesCard hosts={partnerActivities} onOpen={onOpenPartnerActivities} />
      )}
      {view === 'home' && (
        <HomeView
          onCreate={() => {
            // Park (or delete a corrupt) existing tournament before the wizard
            // opens — ensures the user NEVER ends up back in a broken tournament
            // just because one exists in state.
            parkCurrentTournament();
            setTournament(null);
            setView('wizard');
          }}
          onShowPlayers={() => setView('players')}
          onShowArchive={() => setView('archive')}
          onShowSingleMatches={() => setView('createTournament')}
          onOpenSetup={(target) => {
            // Go directly into a tournament setup from the home page (fresh, standalone).
            setSetupFromActivities(false);
            setCustomActivityType(null);
            setPendingSetupLoad(null);
            if (target === 'wizard') {
              parkCurrentTournament();
              setTournament(null);
            } else if (target === 'klubbmesterskapSetup') {
              setEditingKMSetup(null);
            } else if (target === 'singleMatches') {
              setSingleMatchesReturnView('home');
            }
            setView(target);
          }}
          singleMatchesCount={singleMatches.length}
          onShowBookings={() => {
            setBookingInitialDate(null);
            setView('bookings');
          }}
          onShowBookingsOnDate={(iso) => {
            setBookingInitialDate(iso);
            setView('bookings');
          }}
          onShowActivities={() => {
            setActivitiesOpenId(null);
            setView('activities');
          }}
          onShowBanepad={() => setView('banepad')}
          activities={activities}
          activitiesCount={
            (activities || []).filter((a) => {
              if (a.status === 'cancelled' || a.status === 'confirmed') return false;
              if (a.signupClosed) return false;
              const mine = (bookings || []).filter((b) => b.seriesId === a.id);
              const stamps = mine
                .map((b) => new Date(`${b.date}T${b.startTime || '00:00'}:00`).getTime())
                .filter((n) => !isNaN(n));
              let startMs = stamps.length ? Math.min(...stamps) : Infinity;
              if (startMs === Infinity && a.bookingDraft?.slotMode === 'standalone') {
                const r = (a.bookingDraft.slotRooms || [])[0];
                if (r?.singleDate) {
                  const ms = new Date(`${r.singleDate}T${r.startTime || '00:00'}:00`).getTime();
                  if (!isNaN(ms)) startMs = ms;
                }
              }
              const h =
                a.config?.signupCloseHours != null
                  ? Math.max(0, Number(a.config.signupCloseHours) || 0)
                  : 24;
              if (startMs !== Infinity && Date.now() >= startMs - h * 3600000) return false;
              return true;
            }).length
          }
          onOpenActivity={(id) => {
            setActivitiesOpenId(id);
            setView('activities');
          }}
          homeBookings={bookings}
          bookingsCount={bookings.length}
          tournament={tournament}
          onOpenTournament={() =>
            setView(
              tournament?.type === 'americano'
                ? 'americano'
                : tournament?.type === 'mexicano'
                  ? 'mexicano'
                  : tournament?.type === 'mixicano'
                    ? 'mixicano'
                    : tournament?.type === 'team_mexicano'
                      ? 'teamMexicano'
                      : tournament?.type === 'mix_americano'
                        ? 'mixAmericano'
                        : tournament?.type === 'club_americano'
                          ? 'clubAmericano'
                          : tournament?.type === 'club_team_americano'
                            ? 'clubTeamAmericano'
                            : tournament?.type === 'club_mexicano'
                              ? 'clubMexicano'
                              : tournament?.type === 'klubbmesterskap'
                                ? 'klubbmesterskap'
                                : 'tournament',
            )
          }
          onReset={resetTournament}
          onArchiveActive={async () => {
            if (tournament) {
              const result = await archiveTournament(tournament);
              if (result.success) {
                showToast('success', `Turneringen er lagret (${result.sizeKB} KB)`);
                setTournament(null);
              } else {
                showToast('error', `Lagring feilet: ${result.error}`, 8000);
              }
            }
          }}
          archive={combinedArchive}
          archiveLoaded={archiveLoaded}
          onOpenArchived={openArchived}
          onDeleteArchived={deleteArchived}
          effectivePlayerDB={effectivePlayerDB}
          pausedTournaments={pausedTournaments}
          onResumePaused={handleResumeTournament}
          onPauseRunning={handlePauseRunningInList}
          onArchiveFromList={handleArchiveFromList}
          onDeletePaused={handleDeletePaused}
          onDeleteAllPaused={handleDeleteAllPaused}
          drafts={drafts}
          onOpenDraft={openDraft}
          onDeleteDraft={deleteDraft}
          clubInfo={clubInfo}
          bookingConfig={bookingConfig}
          activeSeries={activeSeries}
          onOpenSeries={() => setView('seriespill')}
        />
      )}
      {view === 'createTournament' && (
        <CreateTournamentView
          onBack={goHome}
          onOpenPadel24={() => setView('wizard')}
          onOpenClubChampionship={() => {
            setEditingKMSetup(null);
            setView('klubbmesterskapSetup');
          }}
          onOpenMiniTournaments={() => setView('miniTournaments')}
          onOpenSingleMatch={() => {
            setSingleMatchesReturnView('createTournament');
            setView('singleMatches');
          }}
          onOpenSeries={() => setView(activeSeries ? 'seriespill' : 'seriespillSetup')}
        />
      )}
      {view === 'miniTournaments' && (
        <MiniTournamentsView
          onBack={() => setView('createTournament')}
          onOpenAmericano={() => setView('americanoSetup')}
          onOpenTeamAmericano={() => setView('teamAmericanoSetup')}
          onOpenMexicano={() => setView('mexicanoSetup')}
          onOpenMixicano={() => setView('mixicanoSetup')}
          onOpenTeamMexicano={() => setView('teamMexicanoSetup')}
          onOpenMixAmericano={() => setView('mixAmericanoSetup')}
          onOpenClubAmericano={() => setView('clubAmericanoSetup')}
          onOpenClubTeamAmericano={() => setView('clubTeamAmericanoSetup')}
          onOpenClubMexicano={() => setView('clubMexicanoSetup')}
        />
      )}
      {/* Legacy "otherFormats" — kept as an alias for createTournament to
          avoid a crash if other code still navigates there */}
      {view === 'otherFormats' && (
        <CreateTournamentView
          onBack={goHome}
          onOpenPadel24={() => setView('wizard')}
          onOpenClubChampionship={() => {
            setEditingKMSetup(null);
            setView('klubbmesterskapSetup');
          }}
          onOpenMiniTournaments={() => setView('miniTournaments')}
          onOpenSingleMatch={() => {
            setSingleMatchesReturnView('createTournament');
            setView('singleMatches');
          }}
          onOpenSeries={() => setView(activeSeries ? 'seriespill' : 'seriespillSetup')}
        />
      )}
    </>
  );
}
